"use client";

import { c as _c } from "react/compiler-runtime";
import { Card, CardContent } from "uiShadcn/card";
import { BriefcaseBusiness, Coins } from "lucide-react";
import { type MessageDescriptor, useIntl } from "react-intl";
import { contractTypes, currencyTypes, type SalaryRange, salaryTypes } from "types/salaryRange";
import { TypoText } from "ui/Typo/Typo";
import { ContractTypeMessageIds, CurrencyMessageIds, SalaryTypeMessageIds } from "./constants";
const lowerCaseFirstLetter = (text: string) => text.charAt(0).toLowerCase() + text.slice(1, text.length);
interface SalaryRangeInfoProps {
  salaryRanges?: SalaryRange[];
  benefits?: string;
}
const createTranslationMapper = <TKey extends number, TValue extends string>(formatMessage: (descriptor: MessageDescriptor) => string, typeMap: Record<TKey, TValue>, messages: Record<TValue, MessageDescriptor>) => (id?: TKey | null): string => {
  if (id == null) {
    return "";
  }
  const messageDescriptor = messages[typeMap[id]];
  return messageDescriptor ? formatMessage(messageDescriptor) : "";
};
const SalaryRangeItem: React.FC<{
  range: SalaryRange;
}> = t0 => {
  const $ = _c(36);
  const {
    range
  } = t0;
  const {
    formatMessage,
    formatNumber
  } = useIntl();
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== formatMessage || $[1] !== formatNumber || $[2] !== range.contractTypeId || $[3] !== range.currencyId || $[4] !== range.maxAmount || $[5] !== range.minAmount || $[6] !== range.salaryTypeId) {
    const mapCurrencyToLabel = createTranslationMapper(formatMessage, currencyTypes, CurrencyMessageIds);
    const mapContractToLabel = createTranslationMapper(formatMessage, contractTypes, ContractTypeMessageIds);
    let salaryRangeText;
    let t5;
    if ($[11] !== formatMessage || $[12] !== formatNumber || $[13] !== range.maxAmount || $[14] !== range.minAmount || $[15] !== range.salaryTypeId) {
      const mapSalaryTypeToLabel = createTranslationMapper(formatMessage, salaryTypes, SalaryTypeMessageIds);
      let t6;
      if ($[18] !== formatMessage || $[19] !== formatNumber || $[20] !== range.maxAmount || $[21] !== range.minAmount) {
        t6 = [range.minAmount != null && [formatMessage({
          id: "salary-range.from"
        }), formatNumber(range.minAmount)], range.maxAmount != null && [formatMessage({
          id: "salary-range.to"
        }), formatNumber(range.maxAmount)]].filter(Boolean).flat().join(" ");
        $[18] = formatMessage;
        $[19] = formatNumber;
        $[20] = range.maxAmount;
        $[21] = range.minAmount;
        $[22] = t6;
      } else {
        t6 = $[22];
      }
      salaryRangeText = t6;
      t5 = range.salaryTypeId ? `${mapSalaryTypeToLabel(range.salaryTypeId).toLowerCase()} ${formatMessage({
        id: "salary-range.monthly"
      })}` : "";
      $[11] = formatMessage;
      $[12] = formatNumber;
      $[13] = range.maxAmount;
      $[14] = range.minAmount;
      $[15] = range.salaryTypeId;
      $[16] = salaryRangeText;
      $[17] = t5;
    } else {
      salaryRangeText = $[16];
      t5 = $[17];
    }
    const salaryTypeLabel = t5;
    const currencyLabel = mapCurrencyToLabel(range.currencyId)?.toUpperCase() ?? "";
    const contractLabel = mapContractToLabel(range.contractTypeId) ?? "";
    t1 = salaryRangeText;
    t2 = currencyLabel;
    t3 = salaryTypeLabel;
    t4 = lowerCaseFirstLetter(contractLabel);
    $[0] = formatMessage;
    $[1] = formatNumber;
    $[2] = range.contractTypeId;
    $[3] = range.currencyId;
    $[4] = range.maxAmount;
    $[5] = range.minAmount;
    $[6] = range.salaryTypeId;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
  }
  let t5;
  if ($[23] !== t1 || $[24] !== t2 || $[25] !== t3 || $[26] !== t4) {
    t5 = [t1, t2, t3, t4].filter(Boolean);
    $[23] = t1;
    $[24] = t2;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  const salaryDetails = t5.join(" ");
  let t6;
  if ($[28] !== formatMessage) {
    t6 = formatMessage({
      id: "salary-range.proposed-salary"
    });
    $[28] = formatMessage;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  let t7;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Coins aria-hidden="true" className="h-5 w-5 dark:text-foreground" />;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== salaryDetails) {
    t8 = <div className="flex-1 font-medium text-gray-900 text-sm dark:text-foreground">{salaryDetails}</div>;
    $[31] = salaryDetails;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== t6 || $[34] !== t8) {
    t9 = <li className="flex items-start gap-2" aria-label={t6}>{t7}{t8}</li>;
    $[33] = t6;
    $[34] = t8;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  return t9;
};
const SalaryRangeInfo: React.FC<SalaryRangeInfoProps> = t0 => {
  const $ = _c(26);
  const {
    benefits,
    salaryRanges
  } = t0;
  const {
    formatMessage
  } = useIntl();
  let t1;
  if ($[0] !== benefits) {
    t1 = benefits?.trim();
    $[0] = benefits;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasBenefits = Boolean(t1);
  let T0;
  let T1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[2] !== formatMessage || $[3] !== hasBenefits || $[4] !== salaryRanges) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const validRanges = salaryRanges?.filter(_temp) ?? [];
      if (!validRanges.length && !hasBenefits) {
        t6 = null;
        break bb0;
      }
      T1 = Card;
      t4 = "mt-6 w-full border-gray1 dark:border-foreground dark:bg-background";
      t5 = "salary-range-info";
      T0 = CardContent;
      t2 = "flex flex-col gap-y-2";
      t3 = validRanges.length > 0 && <ul aria-label={formatMessage({
        id: "salary-range.proposed-salary"
      })}>{validRanges.map(_temp2)}</ul>;
    }
    $[2] = formatMessage;
    $[3] = hasBenefits;
    $[4] = salaryRanges;
    $[5] = T0;
    $[6] = T1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    T0 = $[5];
    T1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[12] !== benefits || $[13] !== formatMessage || $[14] !== hasBenefits) {
    t7 = hasBenefits && <div className="flex items-start gap-2" role="group" aria-label={formatMessage({
      id: "salary-range.additional-info"
    })}><BriefcaseBusiness aria-hidden="true" className="h-5 w-5 dark:text-foreground" /><TypoText className="flex-1 text-gray-900 text-sm dark:text-foreground">{benefits}</TypoText></div>;
    $[12] = benefits;
    $[13] = formatMessage;
    $[14] = hasBenefits;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== T0 || $[17] !== t2 || $[18] !== t3 || $[19] !== t7) {
    t8 = <T0 className={t2}>{t3}{t7}</T0>;
    $[16] = T0;
    $[17] = t2;
    $[18] = t3;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== T1 || $[22] !== t4 || $[23] !== t5 || $[24] !== t8) {
    t9 = <T1 className={t4} data-test-id={t5}>{t8}</T1>;
    $[21] = T1;
    $[22] = t4;
    $[23] = t5;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
};
export default SalaryRangeInfo;
function _temp(range) {
  const hasAmount = range.minAmount != null || range.maxAmount != null;
  return hasAmount;
}
function _temp2(range_0) {
  return <SalaryRangeItem key={`${range_0.currencyId}-${range_0.contractTypeId}-${range_0.minAmount}-${range_0.maxAmount}`} range={range_0} />;
}